import { useRef, useState, useCallback, useEffect } from "react";

// MENU-ENFOQUE (docs/plan-corto-menu-enfoque.md): patron de "hover intent"
// generico -- apertura con un pequeño retraso (para no activarse solo por
// cruzar el cursor) y cierre con un margen mayor al salir (para no
// cerrarse por accidente), igual que describe el pedido del usuario
// ("como en Claude"). Un solo hook compartido entre la franja de 12px, el
// panel flotante y el boton de panel -- los tres llaman a los mismos
// onMouseEnter/onMouseLeave, asi que entrar a cualquiera cancela el
// cierre de los demas.
const useHoverIntent = ({ openDelay = 80, closeDelay = 250, disabled = false } = {}) => {
  const [active, setActive] = useState(false);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearTimers = () => {
    if (openTimerRef.current) clearTimeout(openTimerRef.current);
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    openTimerRef.current = null;
    closeTimerRef.current = null;
  };

  const onMouseEnter = useCallback(() => {
    if (disabled) return;
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (active || openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      setActive(true);
    }, openDelay);
  }, [disabled, active, openDelay]);

  const onMouseLeave = useCallback(() => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      setActive(false);
    }, closeDelay);
  }, [closeDelay]);

  const close = useCallback(() => {
    clearTimers();
    setActive(false);
  }, []);

  // Si se desactiva (ya no aplica, p.ej. se volvio a Fijo) nunca debe
  // quedar un "Asomar" fantasma activo.
  useEffect(() => {
    if (disabled) close();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  useEffect(() => clearTimers, []);

  return { active, onMouseEnter, onMouseLeave, close };
};

export default useHoverIntent;
